import { inject } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivateFn, Router, RouterStateSnapshot, UrlTree } from '@angular/router';
import { AuthService } from '../services/auth.service';
import { DialogMessageService } from '../services/dialog-message.service';

/**
 * Functional route guard enforcing granular GARS permissions and role restrictions.
 * Prevents direct URL route access when a feature or permission has been revoked.
 */
export const permissionGuard: CanActivateFn = (
  route: ActivatedRouteSnapshot,
  state: RouterStateSnapshot
): boolean | UrlTree => {
  const authService = inject(AuthService);
  const router = inject(Router);
  const dialogService = inject(DialogMessageService, { optional: true });

  // 1. Super Admin role has unrestricted access across all routes
  if (authService.isSuperAdmin()) {
    return true;
  }

  // 1b. AI Availability Matrix / Feature Matrix is open for all authenticated users
  const moduleId = route.data?.['moduleId'] as string | undefined;
  if (
    moduleId === 'ai-availability' ||
    state.url.includes('feature-matrix') ||
    state.url.includes('ai-availability')
  ) {
    return true;
  }

  // 2. Check adminOnly constraint
  const adminOnly = route.data?.['adminOnly'] as boolean | undefined;
  if (adminOnly && !authService.isAdmin() && !authService.isSuperAdmin()) {
    return denyAccess(router, dialogService, authService, state.url, 'Administrator role required');
  }

  // 3. Check moduleId constraint (dynamic module-level access)
  if (moduleId && !authService.hasModuleAccess(moduleId)) {
    return denyAccess(router, dialogService, authService, state.url, `Access revoked for module: ${moduleId}`);
  }

  // 4. Check single required permission
  const requiredPermission = route.data?.['requiredPermission'] as string | undefined;
  if (requiredPermission && !authService.hasPermission(requiredPermission)) {
    return denyAccess(router, dialogService, authService, state.url, `Permission revoked: ${requiredPermission}`);
  }

  // 5. Check array of required permissions (user needs at least one permitted action)
  const requiredPermissions = route.data?.['requiredPermissions'] as string[] | undefined;
  if (requiredPermissions && requiredPermissions.length > 0) {
    if (!authService.hasAnyPermission(requiredPermissions)) {
      return denyAccess(
        router,
        dialogService,
        authService,
        state.url,
        `Required permissions revoked: [${requiredPermissions.join(', ')}]`
      );
    }
  }

  // 6. Check if all required permissions are strictly required
  const allRequiredPermissions = route.data?.['allRequiredPermissions'] as string[] | undefined;
  if (allRequiredPermissions && allRequiredPermissions.length > 0) {
    const hasAll = allRequiredPermissions.every((perm) => authService.hasPermission(perm));
    if (!hasAll) {
      return denyAccess(
        router,
        dialogService,
        authService,
        state.url,
        `All required permissions must be granted: [${allRequiredPermissions.join(', ')}]`
      );
    }
  }

  return true;
};

/**
 * Denies access, displays notification (if dialog service is available),
 * and redirects to the safe fallback route.
 */
function denyAccess(
  router: Router,
  dialogService: DialogMessageService | null | undefined,
  authService: AuthService,
  attemptedUrl: string,
  reason: string
): UrlTree {
  console.warn(`[PermissionGuard] Access denied to '${attemptedUrl}': ${reason}.`);

  if (dialogService) {
    try {
      setTimeout(() => {
        dialogService.showMessage(
          'warning',
          'Access Restricted',
          'You do not have permission to access the requested module.'
        );
      }, 150);
    } catch {
      // Non-blocking in case of UI teardown or test runner
    }
  }

  const fallbackCandidates = [
    { route: '/app/workspace-analytics', moduleId: 'dashboard' },
    { route: '/app/gemini-intelligence', moduleId: 'gemini-reports' },
    { route: '/app/license-management', moduleId: 'license-monitoring' },
    { route: '/app/feature-matrix', moduleId: 'ai-availability' },
    { route: '/app/access-security', moduleId: 'settings' },
  ];

  const targetCandidate = fallbackCandidates.find(
    c => !attemptedUrl.includes(c.route.replace('/app/', '')) && authService.hasModuleAccess(c.moduleId)
  );

  const target = targetCandidate ? targetCandidate.route : '/login';
  return router.createUrlTree([target]);
}
